import type {
  Action,
  Actor,
  Assertion,
  Credential,
  Diagnostic,
  OverallStatus,
  TrustResolution,
  TrustSnapshot,
  TrustStatus,
  Validity,
  VerificationResult,
} from '@upl/evidence-schema';
import type { CapabilityDescriptor } from '@upl/provenance-core';
import { STATUS_LANGUAGE, UNIVERSAL_LIMITATION, diagnosticLanguage } from '@upl/result-language';

export const HUMAN_REPORT_VERSION = '2.1.0' as const;

export type HumanStatementKind = 'PROVEN' | 'KNOWN' | 'UNKNOWN' | 'WARNING' | 'LIMITATION';
export type HumanAxisId = 'PROVENANCE' | 'INTEGRITY' | 'TRUST' | 'SIGNER' | 'HISTORY';
export type HumanAxisState = 'VERIFIED' | 'PRESENT' | 'INVALID' | 'ABSENT' | 'UNRESOLVED' | 'NOT_RECORDED';

export interface HumanStatement {
  id: string;
  kind: HumanStatementKind;
  text: string;
  traceRefs: readonly string[];
}

export interface HumanAxisSummary {
  id: HumanAxisId;
  label: string;
  state: HumanAxisState;
  explanation: string;
  traceRefs: readonly string[];
}

export interface HumanSignerSummary {
  available: boolean;
  displayName?: string;
  certificateSubject?: string;
  issuer?: string;
  signingTime?: string;
  algorithm?: string;
  identityStatus: 'KNOWN' | 'UNKNOWN' | 'NOT_PROVIDED';
  trustStatuses: readonly TrustStatus[];
  traceRefs: readonly string[];
}

export interface HumanSourceDeclaration {
  id: string;
  sourceType: string;
  label: string;
  basis: 'DECLARED_BY_CREDENTIAL';
  verificationAssessment: 'DECLARATION_DISPLAYED' | 'CREDENTIAL_VALIDATED';
  traceRefs: readonly string[];
}

export interface HumanHistoryEvent {
  id: string;
  sequence: number;
  actionType: string;
  label: string;
  occurredAt?: string;
  actor?: string;
  tool?: string;
  sourceType?: string;
  traceRefs: readonly string[];
}

export interface HumanHistory {
  count: number;
  ordering: 'CHRONOLOGICAL' | 'LOGICAL';
  events: readonly HumanHistoryEvent[];
}

export interface HumanIngredient {
  id: string;
  title: string;
  relationship: string;
  mediaType?: string;
  provenance: 'PRESENT' | 'NOT_RECORDED';
  integrity: Validity;
  branchId: string;
  parentId?: string;
  traceRefs: readonly string[];
}

export interface HumanCapabilityDisclosure {
  id: string;
  label: string;
  state: CapabilityDescriptor['state'];
  description: string;
  mediaTypes: readonly string[];
  local: boolean;
}

export interface HumanTechnicalEvidence {
  signals: VerificationResult['signals'];
  credentials: VerificationResult['credentials'];
  claims: VerificationResult['claims'];
  assertions: VerificationResult['assertions'];
  actions: VerificationResult['actions'];
  actors: VerificationResult['actors'];
  tools: VerificationResult['tools'];
  edges: VerificationResult['evidence_graph']['edges'];
  warnings: VerificationResult['warnings'];
  limitations: VerificationResult['limitations'];
}

export interface HumanEvidenceReport {
  version: typeof HUMAN_REPORT_VERSION;
  status: OverallStatus;
  tone: 'positive' | 'warning' | 'neutral';
  eyebrow: string;
  headline: string;
  summary: string;
  copySummary: string;
  knownFacts: readonly HumanStatement[];
  provenFacts: readonly HumanStatement[];
  unknowns: readonly HumanStatement[];
  warnings: readonly HumanStatement[];
  limitations: readonly HumanStatement[];
  provenance: HumanAxisSummary;
  integrity: HumanAxisSummary & {
    cryptographicValidity: Validity;
    assetBindingValidity: Validity;
  };
  trust: HumanAxisSummary & {
    statuses: readonly TrustStatus[];
    policyName: string;
    policyVersion: string;
    policyId?: string;
    signerTrust: TrustResolution;
    timestampTrust: TrustResolution;
    dataFreshness: 'CURRENT' | 'STALE' | 'NOT_AVAILABLE';
    snapshots: readonly TrustSnapshot[];
  };
  signer: HumanSignerSummary;
  source: readonly HumanSourceDeclaration[];
  history: HumanHistory;
  ingredients: readonly HumanIngredient[];
  credentialSummary: {
    count: number;
    present: boolean;
    cryptographicValidity: Validity;
    assetBindingValidity: Validity;
  };
  axes: readonly HumanAxisSummary[];
  capabilities: readonly HumanCapabilityDisclosure[];
  artifact: VerificationResult['artifact'];
  verification: {
    id: string;
    durationMs: number;
    processedLocally: boolean;
    completedAt: string;
    schemaVersion: string;
    execution: {
      localProcessing: boolean;
      origntdServerProcessing: boolean;
      externalProviderProcessing: boolean;
      mediaTransmitted: boolean;
      destinations: readonly {
        name: string;
        kind: 'ORIGNTD_SERVER' | 'EXTERNAL_SERVICE';
        mediaTransmitted: boolean;
        executionState: 'COMPLETED' | 'FAILED' | 'TIMED_OUT';
      }[];
      origntdSourceMediaRetention: 'NOT_RETAINED' | 'RETAINED';
      externalProviderMediaRetention: 'NOT_APPLICABLE' | 'NOT_ASSERTED';
    };
  };
  technicalEvidence: HumanTechnicalEvidence;
  rawEvidence: VerificationResult;
  providerRawEvidence?: Readonly<Record<string, unknown>>;
  repositoryRegistration?: HumanRepositoryRegistration;
}

export interface HumanRepositoryRegistration {
  repository: 'ORIGNTD';
  registration: 'Recorded';
  receipt: 'Verified';
  registeredAt: string;
  statement: 'Repository registration does not establish factual truth or authorship.';
}

export class HumanReportBuilder {
  readonly #capabilities: readonly CapabilityDescriptor[];

  constructor(capabilities: readonly CapabilityDescriptor[]) {
    this.#capabilities = capabilities;
  }

  build(result: VerificationResult): HumanEvidenceReport {
    const language = STATUS_LANGUAGE[result.overall_status];
    const credential = result.credentials[0];
    const signerActor = findSigner(result, credential);
    const primaryEvidenceRef = credential?.id ?? result.signals[0]?.id ?? result.artifact.id;
    const limitationStatements = buildLimitations(result.limitations, result.overall_status, this.#capabilities);
    const truthLimitation = limitationStatements.find(({ id }) => id.includes('not-factual-truth') || id.includes('not-truth'))
      ?? limitationStatements[0];
    if (!truthLimitation) throw new Error('Human reports require at least one explicit limitation.');

    const history = buildHistory(result.actions, result.actors, result.tools);
    const ingredients = buildIngredients(result.assertions);
    const provenance = buildProvenanceAxis(result, primaryEvidenceRef);
    const integrity = buildIntegrityAxis(result, primaryEvidenceRef);
    const trust = buildTrustAxis(result, primaryEvidenceRef, signerActor, credential);
    const signer = buildSigner(signerActor, credential, result.trust.statuses, primaryEvidenceRef);
    const signerAxis = buildSignerAxis(signer);
    const historyAxis = buildHistoryAxis(history, result.artifact.id);
    const sources = buildSourceDeclarations(result.actions, result.assertions, result.integrity, credential);

    const provenFacts = buildProvenFacts(result, credential);
    const knownFacts = buildKnownFacts(result, credential, signerActor, history, sources);
    const unknowns = buildUnknowns(result, signerActor, history, truthLimitation.id, primaryEvidenceRef);
    const warnings = result.warnings.map(toWarningStatement);
    const axes = [provenance, integrity, trust, signerAxis, historyAxis];
    const capabilities = this.#capabilities.map((capability) => ({
      id: capability.id,
      label: capability.label,
      state: capability.state,
      description: capability.description,
      mediaTypes: capability.mediaTypes,
      local: capability.local,
    }));
    const copySummary = buildCopySummary(language.title, language.explanation, axes, history.count);

    const repositoryRegistration = repositoryRegistrationFromEvidence(result.raw_provider_evidence?.repository_receipt);
    const report: HumanEvidenceReport = {
      version: HUMAN_REPORT_VERSION,
      status: result.overall_status,
      tone: language.tone,
      eyebrow: language.eyebrow,
      headline: language.title,
      summary: language.explanation,
      copySummary,
      knownFacts,
      provenFacts,
      unknowns,
      warnings,
      limitations: limitationStatements,
      provenance,
      integrity,
      trust,
      signer,
      source: sources,
      history,
      ingredients,
      credentialSummary: {
        count: result.credentials.length,
        present: result.credentials.length > 0,
        cryptographicValidity: result.integrity.cryptographic_validity,
        assetBindingValidity: result.integrity.asset_binding_validity,
      },
      axes,
      capabilities,
      artifact: result.artifact,
      verification: {
        id: result.verification_id,
        durationMs: result.processing.duration_ms,
        processedLocally: result.privacy.processed_locally,
        completedAt: result.timestamps.verification_completed_at,
        schemaVersion: result.schema_version,
        execution: executionDisclosure(result),
      },
      technicalEvidence: {
        signals: result.signals,
        credentials: result.credentials,
        claims: result.claims,
        assertions: result.assertions,
        actions: result.actions,
        actors: result.actors,
        tools: result.tools,
        edges: result.evidence_graph.edges,
        warnings: result.warnings,
        limitations: result.limitations,
      },
      rawEvidence: result,
      ...(result.raw_provider_evidence ? { providerRawEvidence: result.raw_provider_evidence } : {}),
      ...(repositoryRegistration ? { repositoryRegistration } : {}),
    };

    const traceabilityErrors = validateHumanReportTraceability(report);
    if (traceabilityErrors.length > 0) {
      throw new Error(`Human report traceability failed: ${traceabilityErrors.join('; ')}`);
    }
    return report;
  }
}

function executionDisclosure(result: VerificationResult): HumanEvidenceReport['verification']['execution'] {
  const disclosure = result.execution_disclosure;
  if (!disclosure) {
    return {
      localProcessing: result.privacy.processed_locally,
      origntdServerProcessing: result.processing.mode === 'REMOTE_EXPLICIT',
      externalProviderProcessing: false,
      mediaTransmitted: result.privacy.uploaded,
      destinations: [],
      origntdSourceMediaRetention: result.privacy.original_media_retained ? 'RETAINED' : 'NOT_RETAINED',
      externalProviderMediaRetention: 'NOT_APPLICABLE',
    };
  }
  return {
    localProcessing: disclosure.local_processing,
    origntdServerProcessing: disclosure.origntd_server_processing,
    externalProviderProcessing: disclosure.external_provider_processing,
    mediaTransmitted: disclosure.media_transmitted,
    destinations: disclosure.destinations.map((destination) => ({
      name: destination.name,
      kind: destination.kind,
      mediaTransmitted: destination.media_transmitted,
      executionState: destination.execution_state,
    })),
    origntdSourceMediaRetention: disclosure.retention.origntd_source_media,
    externalProviderMediaRetention: disclosure.retention.external_provider_media,
  };
}

function repositoryRegistrationFromEvidence(value: unknown): HumanRepositoryRegistration | undefined {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined;
  const record = value as Record<string, unknown>;
  if (record.repository !== 'ORIGNTD' || record.registration !== 'Recorded' || record.receipt !== 'Verified' || typeof record.registeredAt !== 'string') return undefined;
  return {
    repository: 'ORIGNTD',
    registration: 'Recorded',
    receipt: 'Verified',
    registeredAt: record.registeredAt,
    statement: 'Repository registration does not establish factual truth or authorship.',
  };
}

export function buildHumanEvidenceReport(
  result: VerificationResult,
  capabilities: readonly CapabilityDescriptor[],
): HumanEvidenceReport {
  return new HumanReportBuilder(capabilities).build(result);
}

export function validateHumanReportTraceability(report: HumanEvidenceReport): string[] {
  const raw = report.rawEvidence;
  const knownRefs = new Set<string>([
    raw.artifact.id,
    ...raw.signals.map(({ id }) => id),
    ...raw.credentials.map(({ id }) => id),
    ...raw.claims.map(({ id }) => id),
    ...raw.assertions.map(({ id }) => id),
    ...raw.actions.map(({ id }) => id),
    ...raw.actors.map(({ id }) => id),
    ...raw.tools.map(({ id }) => id),
    ...raw.warnings.map(({ id }) => id),
    ...raw.limitations.map(({ id }) => id),
    ...report.limitations.map(({ id }) => id),
  ]);
  const statements = [
    ...report.provenFacts,
    ...report.knownFacts,
    ...report.unknowns,
    ...report.warnings,
    ...report.limitations,
  ];
  const errors: string[] = [];
  for (const statement of statements) {
    if (statement.traceRefs.length === 0) errors.push(`${statement.id} has no trace reference`);
    for (const reference of statement.traceRefs) {
      if (!knownRefs.has(reference)) errors.push(`${statement.id} references unknown ${reference}`);
    }
  }
  return errors;
}

function buildProvenanceAxis(result: VerificationResult, traceRef: string): HumanAxisSummary {
  if (result.overall_status === 'INVALID_OR_TAMPERED') {
    return axis('PROVENANCE', 'Provenance', 'INVALID', 'Provenance information is attached, but validation failed.', [traceRef]);
  }
  if (result.integrity.manifest_presence === 'PRESENT') {
    const state = result.overall_status === 'VERIFIED_PROVENANCE' ? 'VERIFIED' : 'PRESENT';
    return axis('PROVENANCE', 'Provenance', state, 'A supported provenance credential is attached to this file.', [traceRef]);
  }
  if (result.integrity.manifest_presence === 'ABSENT') {
    return axis('PROVENANCE', 'Provenance', 'ABSENT', 'No supported provenance credential was found.', [traceRef]);
  }
  return axis('PROVENANCE', 'Provenance', 'UNRESOLVED', 'Credential presence could not be determined reliably.', [traceRef]);
}

function buildIntegrityAxis(result: VerificationResult, traceRef: string): HumanEvidenceReport['integrity'] {
  const { cryptographic_validity: cryptographicValidity, asset_binding_validity: assetBindingValidity } = result.integrity;
  let state: HumanAxisState = 'UNRESOLVED';
  let explanation = 'Integrity checks did not produce a complete result.';
  if (cryptographicValidity === 'INVALID' || assetBindingValidity === 'INVALID') {
    state = 'INVALID';
    explanation = 'At least one cryptographic or file-binding check failed.';
  } else if (cryptographicValidity === 'VALID' && assetBindingValidity === 'VALID') {
    state = 'VERIFIED';
    explanation = 'The credential signature and its binding to this file validated.';
  } else if (cryptographicValidity === 'NOT_APPLICABLE' && assetBindingValidity === 'NOT_APPLICABLE') {
    state = 'NOT_RECORDED';
    explanation = 'No credential was available for integrity validation.';
  }
  return {
    ...axis('INTEGRITY', 'Integrity', state, explanation, [traceRef]),
    cryptographicValidity,
    assetBindingValidity,
  };
}

function buildTrustAxis(
  result: VerificationResult,
  traceRef: string,
  signerActor: Actor | undefined,
  credential: Credential | undefined,
): HumanEvidenceReport['trust'] {
  const statuses = result.trust.statuses;
  const signerTrust = result.trust.signer_trust
    ?? (statuses.includes('TRUSTED_SIGNER') ? 'TRUSTED' : statuses.includes('UNTRUSTED_CHAIN') ? 'NOT_TRUSTED' : 'UNKNOWN');
  const timestampTrust = result.trust.timestamp_trust
    ?? (statuses.includes('TRUSTED_TIMESTAMP') ? 'TRUSTED' : statuses.includes('UNTRUSTED_TIMESTAMP') ? 'NOT_TRUSTED' : 'UNKNOWN');
  const snapshots = result.trust.snapshots ?? [];
  const dataFreshness = snapshots.length === 0 ? 'NOT_AVAILABLE' : snapshots.some(({ status }) => status === 'STALE') ? 'STALE' : 'CURRENT';
  let state: HumanAxisState = 'UNRESOLVED';
  let explanation = 'Signer trust was not established by the active policy.';
  const signerAvailable = Boolean(signerActor || credential?.signer_actor_id || result.trust.signer_identity);
  if (!signerAvailable || signerTrust === 'NOT_APPLICABLE') {
    state = 'NOT_RECORDED';
    explanation = 'No signer trust evaluation applied because no signer identity was available.';
  } else if (signerTrust === 'TRUSTED') {
    state = 'VERIFIED';
    explanation = result.trust.policy_id === 'C2PA_OFFICIAL_TL'
      ? 'The signer certificate chain matched the official C2PA Trust List.'
      : 'The signer matched the active trust policy.';
  } else if (signerTrust === 'NOT_TRUSTED' || statuses.includes('REVOKED') || statuses.includes('EXPIRED_CREDENTIAL')) {
    state = 'INVALID';
    explanation = 'The signer was not trusted under the active policy; credential integrity remains a separate result.';
  } else if (statuses.includes('KNOWN_SIGNER')) {
    state = 'PRESENT';
    explanation = 'The signer is known, but full trust was not established.';
  } else if (result.trust.trust_list_membership === 'NOT_APPLICABLE') {
    state = 'NOT_RECORDED';
    explanation = 'No signer trust evaluation applied because no credential was available.';
  }
  return {
    ...axis('TRUST', 'Trust', state, explanation, [traceRef]),
    statuses,
    policyName: result.trust.policy_name,
    policyVersion: result.trust.policy_version,
    ...(result.trust.policy_id ? { policyId: result.trust.policy_id } : {}),
    signerTrust,
    timestampTrust,
    dataFreshness,
    snapshots,
  };
}

function buildSignerAxis(signer: HumanSignerSummary): HumanAxisSummary {
  if (!signer.available) return axis('SIGNER', 'Signer', 'NOT_RECORDED', 'No signer identity was provided.', signer.traceRefs);
  if (signer.identityStatus === 'KNOWN') return axis('SIGNER', 'Signer', 'PRESENT', 'A signer identity is declared and recognized by the credential.', signer.traceRefs);
  return axis('SIGNER', 'Signer', 'UNRESOLVED', 'A signer is declared, but identity assurance remains unresolved.', signer.traceRefs);
}

function buildHistoryAxis(history: HumanHistory, artifactId: string): HumanAxisSummary {
  if (history.count === 0) return axis('HISTORY', 'History', 'NOT_RECORDED', 'No supported creation or edit actions were declared.', [artifactId]);
  return axis('HISTORY', 'History', 'PRESENT', `${history.count} declared action${history.count === 1 ? '' : 's'} available.`, history.events.map(({ id }) => id));
}

function axis(
  id: HumanAxisId,
  label: string,
  state: HumanAxisState,
  explanation: string,
  traceRefs: readonly string[],
): HumanAxisSummary {
  return { id, label, state, explanation, traceRefs };
}

function buildSigner(
  actor: Actor | undefined,
  credential: Credential | undefined,
  trustStatuses: readonly TrustStatus[],
  fallbackRef: string,
): HumanSignerSummary {
  if (!actor) {
    return {
      available: false,
      identityStatus: 'NOT_PROVIDED',
      trustStatuses,
      traceRefs: [credential?.id ?? fallbackRef],
    };
  }
  return {
    available: true,
    displayName: actor.name,
    ...(actor.identity ? { certificateSubject: actor.identity } : {}),
    ...(actor.issuer ? { issuer: actor.issuer } : {}),
    ...(credential?.issued_at ? { signingTime: credential.issued_at } : {}),
    ...(actor.signature_algorithm ? { algorithm: actor.signature_algorithm } : {}),
    identityStatus: actor.identity_status,
    trustStatuses,
    traceRefs: [actor.id, ...(credential ? [credential.id] : [])],
  };
}

function findSigner(result: VerificationResult, credential: Credential | undefined): Actor | undefined {
  if (credential?.signer_actor_id) return result.actors.find(({ id }) => id === credential.signer_actor_id);
  return result.actors[0];
}

function buildHistory(actions: readonly Action[], actors: readonly Actor[], tools: VerificationResult['tools']): HumanHistory {
  const chronological = actions.length > 0 && actions.every(({ occurred_at: occurredAt }) => occurredAt !== undefined);
  const orderedActions = chronological
    ? [...actions].sort((left, right) => Date.parse(left.occurred_at ?? '') - Date.parse(right.occurred_at ?? ''))
    : [...actions];
  return {
    count: actions.length,
    ordering: chronological ? 'CHRONOLOGICAL' : 'LOGICAL',
    events: orderedActions.map((action, index) => {
      const actor = actors.find(({ id }) => id === action.actor_id);
      const tool = tools.find(({ id }) => id === action.tool_id);
      return {
        id: action.id,
        sequence: index + 1,
        actionType: action.action_type,
        label: humanize(action.action_type),
        ...(action.occurred_at ? { occurredAt: action.occurred_at } : {}),
        ...(actor ? { actor: actor.name } : {}),
        ...(tool ? { tool: tool.version ? `${tool.name} ${tool.version}` : tool.name } : {}),
        ...(action.source_type ? { sourceType: action.source_type } : {}),
        traceRefs: [action.id],
      };
    }),
  };
}

function buildIngredients(assertions: readonly Assertion[]): HumanIngredient[] {
  return assertions
    .filter(({ label }) => /c2pa\.ingredient/i.test(label))
    .map((assertion, index) => {
      const value = record(assertion.value);
      const title = stringValue(value, 'title', 'label') ?? `Ingredient ${index + 1}`;
      const relationship = stringValue(value, 'relationship') ?? 'component_of';
      const mediaType = stringValue(value, 'format', 'media_type');
      const parentId = stringValue(value, 'parent_id', 'parentId');
      const activeManifest = stringValue(value, 'active_manifest', 'activeManifest');
      return {
        id: assertion.id,
        title,
        relationship,
        ...(mediaType ? { mediaType } : {}),
        provenance: activeManifest ? 'PRESENT' : 'NOT_RECORDED',
        integrity: ingredientValidity(value),
        branchId: parentId ?? assertion.id,
        ...(parentId ? { parentId } : {}),
        traceRefs: [assertion.id],
      };
    });
}

function ingredientValidity(value: Record<string, unknown>): Validity {
  const state = stringValue(value, 'validation_state', 'validationState')?.toLowerCase();
  if (state === 'invalid') return 'INVALID';
  if (state === 'valid' || state === 'trusted') return 'VALID';
  const statuses = arrayValue(value, 'validation_status', 'validationStatus');
  if (statuses.some((entry) => record(entry).success === false)) return 'INVALID';
  if (statuses.some((entry) => record(entry).success === true)) return 'VALID';
  return 'UNKNOWN';
}

function buildSourceDeclarations(
  actions: readonly Action[],
  assertions: readonly Assertion[],
  integrity: VerificationResult['integrity'],
  credential: Credential | undefined,
): HumanSourceDeclaration[] {
  const declarations = new Map<string, string>();
  for (const action of actions) {
    if (action.source_type) declarations.set(action.source_type, action.id);
  }
  for (const assertion of assertions) collectSourceTypes(assertion.value, assertion.id, declarations);
  return [...declarations].map(([sourceType, traceRef], index) => ({
    id: `source:${index + 1}`,
    sourceType,
    label: sourceTypeLabel(sourceType),
    basis: 'DECLARED_BY_CREDENTIAL',
    verificationAssessment: credential && integrity.cryptographic_validity === 'VALID' && integrity.asset_binding_validity === 'VALID'
      ? 'CREDENTIAL_VALIDATED'
      : 'DECLARATION_DISPLAYED',
    traceRefs: [traceRef],
  }));
}

function collectSourceTypes(value: unknown, traceRef: string, output: Map<string, string>, depth = 0): void {
  if (depth > 5) return;
  if (Array.isArray(value)) {
    for (const entry of value) collectSourceTypes(entry, traceRef, output, depth + 1);
    return;
  }
  const item = record(value);
  for (const [key, entry] of Object.entries(item)) {
    const normalizedKey = key.replaceAll('_', '').toLowerCase();
    if ((normalizedKey === 'digitalsourcetype' || normalizedKey === 'sourcetype') && typeof entry === 'string') {
      output.set(entry, traceRef);
    } else if (typeof entry === 'object' && entry !== null) {
      collectSourceTypes(entry, traceRef, output, depth + 1);
    }
  }
}

function buildProvenFacts(result: VerificationResult, credential: Credential | undefined): HumanStatement[] {
  const statements: HumanStatement[] = [{
    id: 'fact:artifact-fingerprint',
    kind: 'PROVEN',
    text: 'A SHA-256 fingerprint was computed for the locally inspected file.',
    traceRefs: [result.artifact.id],
  }];
  if (result.privacy.processed_locally && !result.privacy.uploaded && !result.privacy.stored) {
    statements.push({
      id: 'fact:local-processing',
      kind: 'PROVEN',
      text: 'This verification completed locally without uploading or storing the file.',
      traceRefs: [result.artifact.id],
    });
  }
  if (credential && credential.cryptographic_validity === 'VALID') {
    statements.push({ id: 'fact:signature-valid', kind: 'PROVEN', text: 'The credential signature validated.', traceRefs: [credential.id] });
  }
  if (credential && credential.asset_binding_validity === 'VALID') {
    statements.push({ id: 'fact:binding-valid', kind: 'PROVEN', text: 'The credential is cryptographically bound to this file.', traceRefs: [credential.id] });
  }
  const signerTrustSignal = result.signals.find(({ kind }) => kind === 'C2PA_SIGNER_TRUST');
  if (result.trust.signer_trust === 'TRUSTED' && signerTrustSignal) {
    statements.push({
      id: 'fact:official-signer-trust',
      kind: 'PROVEN',
      text: 'The signer certificate chain matched the official C2PA Trust List snapshot recorded in this result.',
      traceRefs: [signerTrustSignal.id],
    });
  }
  const timestampTrustSignal = result.signals.find(({ kind }) => kind === 'C2PA_TIMESTAMP_TRUST');
  if (result.trust.timestamp_trust === 'TRUSTED' && timestampTrustSignal) {
    statements.push({
      id: 'fact:official-timestamp-trust',
      kind: 'PROVEN',
      text: 'The timestamp authority matched the official C2PA TSA Trust List snapshot recorded in this result.',
      traceRefs: [timestampTrustSignal.id],
    });
  }
  return statements;
}

function buildKnownFacts(
  result: VerificationResult,
  credential: Credential | undefined,
  signer: Actor | undefined,
  history: HumanHistory,
  sources: readonly HumanSourceDeclaration[],
): HumanStatement[] {
  const statements: HumanStatement[] = [];
  if (credential) statements.push({ id: 'fact:credential-present', kind: 'KNOWN', text: 'A supported provenance credential is present.', traceRefs: [credential.id] });
  for (const signal of result.signals.filter(({ state, kind }) => state !== 'ABSENT' && kind !== 'C2PA_VALIDATION')) {
    statements.push({ id: `fact:${signal.id}`, kind: 'KNOWN', text: signal.label, traceRefs: [signal.id] });
  }
  if (signer) statements.push({ id: 'fact:signer-declared', kind: 'KNOWN', text: `The credential declares the signer “${signer.name}”.`, traceRefs: [signer.id] });
  if (history.count > 0) statements.push({ id: 'fact:history-declared', kind: 'KNOWN', text: `${history.count} creation or edit action${history.count === 1 ? ' is' : 's are'} declared.`, traceRefs: history.events.map(({ id }) => id) });
  for (const source of sources) statements.push({ id: `fact:${source.id}`, kind: 'KNOWN', text: `The credential declares the digital source type “${source.label}”.`, traceRefs: source.traceRefs });
  return statements;
}

function buildUnknowns(
  result: VerificationResult,
  signer: Actor | undefined,
  history: HumanHistory,
  truthLimitationId: string,
  primaryEvidenceRef: string,
): HumanStatement[] {
  const statements: HumanStatement[] = [{
    id: 'unknown:factual-truth',
    kind: 'UNKNOWN',
    text: 'The factual accuracy of the depicted content is not determined by provenance evidence.',
    traceRefs: [truthLimitationId],
  }];
  if (!signer) statements.push({ id: 'unknown:signer', kind: 'UNKNOWN', text: 'No supported signer identity was provided.', traceRefs: [primaryEvidenceRef] });
  if (history.count === 0) statements.push({ id: 'unknown:history', kind: 'UNKNOWN', text: 'No supported creation or edit history was declared.', traceRefs: [result.artifact.id] });
  else if (history.ordering === 'LOGICAL') statements.push({ id: 'unknown:history-time', kind: 'UNKNOWN', text: 'One or more action timestamps were not provided, so the sequence is logical rather than chronological.', traceRefs: history.events.map(({ id }) => id) });
  return statements;
}

function buildLimitations(
  diagnostics: readonly Diagnostic[],
  status: OverallStatus,
  capabilities: readonly CapabilityDescriptor[],
): HumanStatement[] {
  const statements = diagnostics
    .filter(({ code }) => code !== 'ABSENCE_NOT_AUTHORSHIP' || status === 'NO_SUPPORTED_PROVENANCE')
    .map((diagnostic) => ({
      id: diagnostic.id,
      kind: 'LIMITATION' as const,
      text: diagnostic.code === 'NOT_FACTUAL_TRUTH' ? UNIVERSAL_LIMITATION : diagnosticLanguage(diagnostic.code),
      traceRefs: [diagnostic.id, ...diagnostic.evidence_refs],
    }));
  for (const capability of capabilities.filter(({ state }) => state === 'NOT_CHECKED')) {
    const id = `limitation:capability:${capability.id}`;
    statements.push({ id, kind: 'LIMITATION', text: capability.description, traceRefs: [id] });
  }
  return dedupeStatements(statements);
}

function toWarningStatement(diagnostic: Diagnostic): HumanStatement {
  return {
    id: diagnostic.id,
    kind: 'WARNING',
    text: diagnosticLanguage(diagnostic.code),
    traceRefs: [diagnostic.id, ...diagnostic.evidence_refs],
  };
}

function dedupeStatements(statements: readonly HumanStatement[]): HumanStatement[] {
  return [...new Map(statements.map((statement) => [statement.id, statement])).values()];
}

function buildCopySummary(
  headline: string,
  summary: string,
  axes: readonly HumanAxisSummary[],
  historyCount: number,
): string {
  const axisCopy = axes.map(({ label, state }) => `${label}: ${humanize(state)}`).join(' · ');
  return `${headline}\n${summary}\n${axisCopy}\nHistory entries: ${historyCount}\n${UNIVERSAL_LIMITATION}`;
}

function record(value: unknown): Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value) ? value as Record<string, unknown> : {};
}

function stringValue(value: Record<string, unknown>, ...keys: string[]): string | undefined {
  for (const key of keys) {
    const entry = value[key];
    if (typeof entry === 'string' && entry.length > 0) return entry;
  }
  return undefined;
}

function arrayValue(value: Record<string, unknown>, ...keys: string[]): unknown[] {
  for (const key of keys) {
    const entry = value[key];
    if (Array.isArray(entry)) return entry;
  }
  return [];
}

function humanize(value: string): string {
  return value
    .replace(/^c2pa\./, '')
    .replaceAll('_', ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .toLowerCase()
    .replace(/^./, (letter) => letter.toUpperCase());
}

function sourceTypeLabel(value: string): string {
  const vocabularyTerm = value.split(/[/#]/).filter(Boolean).at(-1) ?? value;
  const labels: Readonly<Record<string, string>> = {
    algorithmicMedia: 'Algorithmic media',
    trainedAlgorithmicMedia: 'Trained algorithmic media',
    digitalCapture: 'Digital capture',
    composite: 'Composite media',
  };
  return labels[vocabularyTerm] ?? humanize(vocabularyTerm);
}
